// Service type: Hauswirtschaft (HOME_CARE) vs. 24h-Betreuung (LIVE_IN_CARE)
export enum ServiceType {
  HOME_CARE = 'HOME_CARE',
  LIVE_IN_CARE = 'LIVE_IN_CARE',
}

// Prospect (potential 24h customer identified by geo-search)
export enum ProspectStatus {
  NEW = 'NEW',
  QUALIFIED = 'QUALIFIED',
  CONTACTED = 'CONTACTED',
  NEGOTIATING = 'NEGOTIATING',
  WON = 'WON',
  LOST = 'LOST',
  DISQUALIFIED = 'DISQUALIFIED',
}

export const PROSPECT_STATUS_LABELS: Record<ProspectStatus, string> = {
  [ProspectStatus.NEW]: 'Neu',
  [ProspectStatus.QUALIFIED]: 'Qualifiziert',
  [ProspectStatus.CONTACTED]: 'Kontaktiert',
  [ProspectStatus.NEGOTIATING]: 'In Verhandlung',
  [ProspectStatus.WON]: 'Gewonnen',
  [ProspectStatus.LOST]: 'Verloren',
  [ProspectStatus.DISQUALIFIED]: 'Disqualifiziert',
};

export const PROSPECT_STATUS_COLORS: Record<ProspectStatus, string> = {
  [ProspectStatus.NEW]: '#06b6d4',          // cyan — kräftig, kontrastiert mit Satellit/Vegetation
  [ProspectStatus.QUALIFIED]: '#3b82f6',    // blue
  [ProspectStatus.CONTACTED]: '#8b5cf6',    // purple
  [ProspectStatus.NEGOTIATING]: '#f59e0b',  // amber
  [ProspectStatus.WON]: '#10b981',          // emerald
  [ProspectStatus.LOST]: '#ef4444',         // red
  [ProspectStatus.DISQUALIFIED]: '#6b7280', // gray
};

export interface ProspectFootprint {
  type: 'Polygon';
  coordinates: number[][][]; // GeoJSON Polygon: [[ [lon, lat], ... ]]
}

export interface Prospect {
  id: number;
  externalRef?: string;
  sourceCustomerId?: number;
  distanceM?: number;
  footprintGeoJson?: ProspectFootprint;
  centroidLat?: number;
  centroidLon?: number;
  buildingFootprintM2?: number;
  parcelM2?: number;
  freeAreaM2?: number;
  grz?: number;
  gfk?: string;
  bodenrichtwert?: number;
  scorePercent?: number;
  street?: string;
  houseNumber?: string;
  postalCode?: string;
  city?: string;
  status: ProspectStatus;
  ownerName?: string;
  ownerBirthDate?: string; // ISO yyyy-MM-dd
  notes?: string;
  convertedCustomerId?: number;
  createdAt?: string;
  updatedAt?: string;
}

export const SERVICE_TYPE_LABELS: Record<ServiceType, string> = {
  [ServiceType.HOME_CARE]: 'Hauswirtschaft',
  [ServiceType.LIVE_IN_CARE]: '24h-Betreuung',
};

// Employee types
export interface WorkingDay {
  id?: number;
  employeeId: number;
  dayOfWeek: number; // 1=Monday, 7=Sunday
  startTime: string; // HH:mm format
  endTime: string; // HH:mm format
}

export interface Employee {
  id?: number;
  personnelNumber?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  street?: string;
  houseNumber?: string;
  postalCode?: string;
  city?: string;
  latitude?: number;
  longitude?: number;
  maxRadius: number;
  color?: string;
  workingDays: WorkingDay[];
  active: boolean;
  createdAt?: string;
  updatedAt?: string;
  serviceType: ServiceType;
}

// User-extensible status definitions for carers and families. The carer/family `status`
// field stores the stable `statusKey`; labels and colors are managed via the Settings page.
export type StatusDomain = 'CARER' | 'FAMILY';

export interface StatusDefinition {
  id?: number;
  domain: StatusDomain;
  statusKey?: string;
  label: string;
  color: string;
  sortOrder?: number;
}

// Stable key of the "Dame ist verfügbar" carer status (seeded), used for the dashboard count.
export const CARER_STATUS_AVAILABLE_KEY = 'DAME_VERFUEGBAR';

// Customer types
export enum FrequencyType {
  WEEKLY = 'WEEKLY',
  BIWEEKLY = 'BIWEEKLY',
  MONTHLY = 'MONTHLY',
  CUSTOM = 'CUSTOM'
}

export interface VisitFrequency {
  id?: number;
  customerId: number;
  frequencyType: FrequencyType;
  frequencyValue: number;
  preferredDays?: string; // comma-separated string
}

export interface Customer {
  id?: number;
  customerNumber?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  street?: string;
  houseNumber?: string;
  postalCode?: string;
  city?: string;
  latitude?: number;
  longitude?: number;
  visitDuration?: number; // in minutes
  notes?: string;
  active: boolean;
  createdAt?: string;
  updatedAt?: string;
  visitFrequency?: VisitFrequency;
  serviceType: ServiceType;
  // Angaben für den Leistungsnachweis
  birthDate?: string;        // JJJJ-MM-TT
  deathDate?: string;        // JJJJ-MM-TT
  insuranceNumber?: string;  // Versichertennummer
  careLevel?: number;        // Pflegegrad 1–5
  healthInsurance?: string;  // Krankenkasse
  kvnr?: string;             // Krankenversichertennummer, z. B. A123456789
}

// ─────────────────────────────────────────────────────────────────────────
// 24h domain (carers / families / placements) — separate from HW employees/customers
// ─────────────────────────────────────────────────────────────────────────

export interface Carer {
  id?: number;
  externalId?: number;
  firstName?: string;
  lastName?: string;
  phone?: string;
  email?: string;
  gender?: string;
  status?: string; // StatusDefinition.statusKey (domain CARER)
  availableFrom?: string; // ISO yyyy-MM-dd
  coordinator?: string;
  color?: string;
  active: boolean;
  createdAt?: string;
  updatedAt?: string;
  // Transient: current placement
  currentFamilyId?: number;
  currentFamilyName?: string;
}

export interface PhoneNumber {
  label?: string;
  number: string;
}

export interface FamilyContact {
  id?: number;
  familyId?: number;
  firstName?: string;
  lastName?: string;
  phones?: PhoneNumber[];
  email?: string;
  street?: string;
  houseNumber?: string;
  postalCode?: string;
  city?: string;
  relationship?: string;
  notes?: string;
}

// Eine gepflegte Person ("Klient") innerhalb einer Familie — eigener Name,
// Geburtsdatum und Pflegegrad. Eine Familie kann mehrere Klienten haben (z. B. Ehepaar).
export interface Klient {
  id?: number;
  familyId?: number;
  firstName?: string;
  lastName?: string;
  birthDate?: string; // ISO yyyy-MM-dd
  pflegegrad?: string;
  notes?: string;
}

export interface Family {
  id?: number;
  customerNumber?: string;
  familyName?: string;
  phones?: PhoneNumber[];
  email?: string;
  street?: string;
  houseNumber?: string;
  postalCode?: string;
  city?: string;
  latitude?: number;
  longitude?: number;
  vertragspartner?: string;
  careStartDate?: string; // ISO yyyy-MM-dd
  status?: string; // StatusDefinition.statusKey (domain FAMILY)
  koordinatorin?: string;
  prospectSearchRadiusM?: number;
  notes?: string;
  active: boolean;
  createdAt?: string;
  updatedAt?: string;
  // Transient
  klienten?: Klient[];
  contacts?: FamilyContact[];
  currentCarerId?: number;
  currentCarerName?: string;
}

export interface Placement {
  id?: number;
  carerId: number;
  familyId?: number;
  startDate?: string; // ISO yyyy-MM-dd
  endDate?: string;   // ISO yyyy-MM-dd, null/undefined = ongoing
  notes?: string;
  // Transient resolved names
  carerName?: string;
  familyName?: string;
}

// Tagespflegeeinrichtung — Kooperationspartner der 24h-Betreuung. `radiusM` ist der
// Aktionsradius der Einrichtung; welche Familien darin liegen, rechnet der Server aus.
export interface Tagespflege {
  id?: number;
  name: string;
  traeger?: string;
  street?: string;
  houseNumber?: string;
  postalCode?: string;
  city?: string;
  latitude?: number;
  longitude?: number;
  radiusM: number;
  contactPerson?: string;
  phone?: string;
  email?: string;
  website?: string;
  notes?: string;
  active: boolean;
  createdAt?: string;
  updatedAt?: string;
  // Transient: Anzahl aktiver Familien im Aktionsradius (vom Server berechnet).
  familienImUmkreis?: number;
}

// Vorgabe und Grenzen des Aktionsradius — identisch mit Tagespflege.kt / V44.
export const TAGESPFLEGE_DEFAULT_RADIUS_M = 15000;
export const TAGESPFLEGE_MIN_RADIUS_M = 500;
export const TAGESPFLEGE_MAX_RADIUS_M = 50000;

// Eine Familie im Aktionsradius einer Tagespflege.
export interface TagespflegeFamilie {
  familyId: number;
  familyName?: string;
  customerNumber?: string;
  address: string;
  latitude: number;
  longitude: number;
  distanceM: number;
  status?: string; // StatusDefinition.statusKey (domain FAMILY)
  pflegegrade?: string;
  klienten?: string;
}

// Visit types
export enum VisitStatus {
  SCHEDULED = 'SCHEDULED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
  SKIPPED = 'SKIPPED'
}

export interface Visit {
  id?: number;
  employeeId: number;
  customerId: number | null;
  scheduledStart: string;
  scheduledEnd: string;
  status: VisitStatus;
  title?: string;
  estimatedTravelTime?: number;
  estimatedDistance?: number;
  routeGeometry?: string;
  notes?: string;
  /** Gesetzt, solange der Besuch als Vertretung oder Tausch bei einem anderen Mitarbeiter liegt. */
  originalEmployeeId?: number | null;
  absenceId?: number | null;
  substitutionForVisitId?: number | null;
  substitutedBy?: string | null;
  substitutedAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface Route {
  id?: number;
  employeeId: number;
  date: string;
  visits: Visit[];
  totalDistance?: number;
  totalDuration?: number;
  optimized: boolean;
  createdAt?: string;
}

export interface CreateVisitRequest {
  customerId: number | null;
  employeeId: number;
  scheduledStart: string;
  scheduledEnd: string;
  title?: string;
  notes?: string;
  isRecurring?: boolean;
  recurringType?: 'WEEKLY' | 'BIWEEKLY' | 'MONTHLY';
  recurringInterval?: number;
  recurringEndDate?: string;
}

// Assignment types
export interface EmployeeCustomerAssignment {
  id?: number;
  employeeId: number;
  customerId: number;
  assignedAt?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface EmployeeWithCustomers {
  employee: Employee;
  customers: Customer[];
}

export interface CustomerWithEmployee {
  customer: Customer;
  employee?: Employee;
}

// Form types
export interface EmployeeFormData {
  personnelNumber?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  street?: string;
  houseNumber?: string;
  postalCode?: string;
  city?: string;
  latitude?: number;
  longitude?: number;
  maxRadius: number;
  color: string;
  workingDays: {
    [key: number]: {
      enabled: boolean;
      startTime: string;
      endTime: string;
    };
  };
}

export interface CustomerFormData {
  customerNumber?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  street?: string;
  houseNumber?: string;
  postalCode?: string;
  city?: string;
  latitude?: number;
  longitude?: number;
  visitDuration?: number;
  notes?: string;
  frequencyType: FrequencyType;
  frequencyValue: number;
  preferredDays: number[];
  birthDate?: string;
  deathDate?: string;
  insuranceNumber?: string;
  careLevel?: string;
  healthInsurance?: string;
  kvnr?: string;
}

// Authentication types
export enum UserRole {
  USER = 'USER',
  ADMIN = 'ADMIN'
}

export interface User {
  id: number;
  firstName: string;
  lastName: string;
  username: string;
  role: UserRole;
  active: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface AuthResponse {
  user: User;
  accessToken: string;
  refreshToken: string;
}

export interface LoginRequest {
  username: string;
  password: string;
}

export interface CreateUserRequest {
  firstName: string;
  lastName: string;
  username: string;
  password: string;
  role: UserRole;
}

export interface UpdateUserRequest {
  firstName: string;
  lastName: string;
  username: string;
  password?: string;
  role: UserRole;
  active: boolean;
}

export interface ChangePasswordRequest {
  oldPassword: string;
  newPassword: string;
}

export type CommentEntityType = 'CUSTOMER' | 'CARER' | 'FAMILY';

export interface Comment {
  id: number;
  entityType: CommentEntityType;
  entityId: number;
  authorUsername: string;
  authorName: string;
  content: string;
  edited: boolean;
  createdAt: string;
  updatedAt: string;
}

export type DocumentEntityType = 'CUSTOMER' | 'CARER' | 'FAMILY';

export type DocumentType = 'VERTRAG' | 'RECHNUNG' | 'AUSWEIS' | 'QUALIFIKATION' | 'FOTO' | 'SONSTIGES';

export const DOCUMENT_TYPES: { value: DocumentType; label: string }[] = [
  { value: 'VERTRAG', label: 'Vertrag' },
  { value: 'RECHNUNG', label: 'Rechnung' },
  { value: 'AUSWEIS', label: 'Ausweis' },
  { value: 'QUALIFIKATION', label: 'Qualifikation' },
  { value: 'FOTO', label: 'Foto' },
  { value: 'SONSTIGES', label: 'Sonstiges' },
];

export interface Document {
  id: number;
  entityType: DocumentEntityType;
  entityId: number;
  documentType: DocumentType;
  filename: string;
  contentType: string;
  sizeBytes: number;
  authorUsername: string;
  authorName: string;
  createdAt: string;
  updatedAt: string;
}
// ── Anfragen von der öffentlichen Website ───────────────────────────────────

export type ContactRequestStatus = 'NEW' | 'IN_PROGRESS' | 'DONE' | 'NO_INTEREST' | 'SPAM';

export type ContactRequestTopic = 'CONTACT' | 'CARE_24H' | 'HOME_CARE' | 'COSTS' | 'CAREER';

/** Öffentlicher Auftritt, über den eine Anfrage einging (`ContactRequestSite` im Backend). */
export type ContactRequestSite = 'ANTCZAK' | 'MITEINANDER38';

/**
 * Statusliste des Posteingangs. Die Farben werden als Textfarbe auf hellem Grund
 * verwendet und erfüllen alle WCAG AA (>= 4.5:1 gegen Weiß); die Bedeutung trägt
 * immer die Beschriftung, die Farbe unterstützt sie nur.
 */
export const CONTACT_REQUEST_STATUSES: {
  value: ContactRequestStatus;
  label: string;
  color: string;
  /** Zählt als offene Aufgabe im Posteingang. */
  open: boolean;
}[] = [
  { value: 'NEW', label: 'Neu', color: '#1d4ed8', open: true },
  { value: 'IN_PROGRESS', label: 'In Bearbeitung', color: '#a16207', open: true },
  { value: 'DONE', label: 'Erledigt', color: '#15803d', open: false },
  { value: 'NO_INTEREST', label: 'Kein Interesse', color: '#475569', open: false },
  { value: 'SPAM', label: 'Spam', color: '#b91c1c', open: false },
];

export const CONTACT_REQUEST_TOPICS: { value: ContactRequestTopic; label: string }[] = [
  { value: 'CONTACT', label: 'Allgemeine Anfrage' },
  { value: 'CARE_24H', label: '24-Stunden-Betreuung' },
  { value: 'HOME_CARE', label: 'Hauswirtschaft' },
  { value: 'COSTS', label: 'Kosten / Angebot' },
  { value: 'CAREER', label: 'Bewerbung' },
];

/**
 * Die Auftritte für den Posteingang. Die Beschriftung ist bewusst kürzer als der
 * Name aus `sites.json` — in einer Tabellenzelle zählt, dass die beiden Quellen auf
 * einen Blick auseinanderzuhalten sind, nicht der vollständige Firmenname.
 */
export const CONTACT_REQUEST_SITES: {
  value: ContactRequestSite;
  label: string;
  /** Für die Detailansicht: von welcher Adresse die Anfrage kam. */
  host: string;
  color: string;
}[] = [
  { value: 'ANTCZAK', label: 'Website', host: 'sb-antczak.de', color: '#0f766e' },
  { value: 'MITEINANDER38', label: 'Miteinander38', host: 'miteinander38.de', color: '#7c3aed' },
];

export interface ContactRequest {
  id: number;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  message: string;
  topic: ContactRequestTopic;
  site: ContactRequestSite;
  sourcePath: string | null;
  /**
   * Antworten auf die Zusatzfragen des Kontaktformulars. `null` bei Anliegen, die
   * keine stellen (etwa einer Bewerbung), und bei Anfragen aus der Zeit davor.
   */
  details: Record<string, string> | null;
  status: ContactRequestStatus;
  assignedTo: string | null;
  internalNote: string | null;
  createdAt: string;
  updatedAt: string;
  firstResponseAt: string | null;
  closedAt: string | null;
}

export interface ContactRequestUpdate {
  status?: ContactRequestStatus;
  /** Leerstring hebt die Zuweisung auf. */
  assignedTo?: string;
  /** Leerstring löscht die Notiz. */
  internalNote?: string;
}

export interface ContactRequestStats {
  total: number;
  open: number;
  last7Days: number;
  last30Days: number;
  previous30Days: number;
  byStatus: { key: string; count: number }[];
  byTopic: { key: string; count: number }[];
  bySite: { key: string; count: number }[];
  byMonth: { month: string; count: number }[];
  avgFirstResponseHours: number | null;
}
